import cloneDeep from 'lodash/cloneDeep';
import {expandToAnomalyDetails, injectAnomalyValue} from './utils/common';

import {generatePermalink} from './utils/permalink';
import {trackingProperties} from './utils/tracking';

export const ALERT_TYPES = {
  anomaly: `anomaly`,
  contour: `contour`,
};

export class Alert {
  constructor(alert, projectId, workspaceId, type) {
    this.projectId = projectId;
    this.raw = alert;
    this.workspaceId = workspaceId;
    this.type = type;
  }

  get alertId() {
    return this.raw.alertId;
  }

  get hasExplains() {
    return this.explains && this.explains.length > 0;
  }

  get isAlertOwner() {
    return this.raw.isAlertOwner;
  }

  get isRead() {
    return this.raw.isRead;
  }

  get timestamp() {
    return undefined;
  }

  get ingestedAtMs() {
    return this.raw.ingestedAtMs;
  }

  get trackingProperties() {
    return {
      ...trackingProperties(this.raw),
      platform_created: `WEB`,
      platform_detected: `WEB`,
      project_id: this.projectId,
      has_explains: this.hasExplains,
      alert_version: `2`,
    };
  }

  generatePermalink(absolute = false, shared = false) {
    return generatePermalink(
      {
        alert: this.raw,
        projectId: this.projectId,
        shared,
        workspaceId: this.workspaceId,
      },
      absolute,
    );
  }

  static create(alert, projectId, workspaceId) {
    if (alert.anomaly) {
      return Anomaly.create(alert, projectId, workspaceId);
    } else {
      return new Contour(alert, projectId, workspaceId);
    }
  }
}

export class Anomaly extends Alert {
  constructor(alert, projectId, workspaceId) {
    super(alert, projectId, workspaceId, ALERT_TYPES.anomaly);
  }

  static create(alert, projectId, workspaceId) {
    const {anomalyDetail, anomalyDetails} = alert.anomaly;
    const anomaly = anomalyDetail || anomalyDetails[0];

    if (`funnelsDetails` in anomaly) {
      return new FunnelsAnomaly(alert, projectId, workspaceId);
    } else {
      return new Anomaly(alert, projectId, workspaceId);
    }
  }

  get anomaly() {
    return this.raw.anomaly;
  }

  get anomalyDetail() {
    const {anomalyDetail, anomalyDetails} = this.anomaly;
    if (anomalyDetail) {
      return anomalyDetail;
    } else {
      return anomalyDetails[0];
    }
  }

  get explains() {
    if (this.anomalyDetail.explains) {
      return this.anomalyDetail.explains.map((ex) => new Explanation(this.raw, this.projectId, ex, this.workspaceId));
    }
    return [];
  }

  get explainsCount() {
    return this.explains.length;
  }

  get expectedValue() {
    return this.anomalyDetail.expectedValue;
  }

  get valueAtTimestamp() {
    return this.anomalyDetail.valueAtTimestamp;
  }

  get timestamp() {
    return this.anomalyDetail.anomalyTimestamp;
  }

  get trackingProperties() {
    return {
      ...super.trackingProperties,
      number_of_explains: this.explainsCount,
    };
  }

  get anomalyDirection() {
    return this.anomalyDetail.direction;
  }

  get expandedToAnomalyDetails() {
    return expandToAnomalyDetails(this.raw);
  }

  projectTimestamp(utcOffset) {
    return Number(this.anomalyDetail.anomalyTimestamp) + utcOffset * 60000;
  }

  injectAnomalyValue(value) {
    return injectAnomalyValue(this.raw, value);
  }

  isInsightsAnomaly() {
    const {insightsQuery} = this.anomaly;
    return !!insightsQuery;
  }

  findExplains(property, propertyValue) {
    return this.explains.find((ex) => ex.property === property && ex.propertyValue === propertyValue);
  }
}

export class FunnelsAnomaly extends Anomaly {
  findAnomalyForStepIndex(stepIndex, startDate, endDate) {
    return this.expandedToAnomalyDetails.find((detailAlert) => {
      const anomalyDetail = detailAlert.anomaly.anomalyDetail;
      const anomalyTimestamp = Number(anomalyDetail.anomalyTimestamp);
      if (anomalyTimestamp < startDate || anomalyTimestamp > endDate) {
        return false;
      }

      const funnelsDetails = anomalyDetail.funnelsDetails;
      return funnelsDetails.startStepIdx === stepIndex;
    });
  }
}

export class Contour extends Alert {
  constructor(alert, projectId, workspaceId) {
    super(alert, projectId, workspaceId, ALERT_TYPES.contour);
  }

  get contour() {
    return this.raw.contour;
  }

  get timestamp() {
    return this.ingestedAtMs;
  }
}

export class Explanation {
  constructor(alert, projectId, explains, workspaceId) {
    this.alert = alert;
    this.projectId = projectId;
    this.explains = explains;
    this.workspaceId = workspaceId;
  }

  get priority() {
    return this.explains.priority;
  }

  get propertySegmentsDetail() {
    return this.explains.propertySegmentsDetail;
  }

  get anomalousUsersDetail() {
    return this.explains.anomalousUsersDetail;
  }

  get property() {
    if (this.propertySegmentsDetail) {
      return this.propertySegmentsDetail.property;
    } else {
      return `User`;
    }
  }

  get propertyValue() {
    if (this.propertySegmentsDetail) {
      if (`stringValue` in this.propertySegmentsDetail) {
        return this.propertySegmentsDetail.stringValue;
      } else if (`boolValue` in this.propertySegmentsDetail) {
        return this.propertySegmentsDetail.boolValue;
      } else if (`numValue` in this.propertySegmentsDetail) {
        return this.propertySegmentsDetail.numValue;
      }
      return undefined;
    } else {
      return this.anomalousUsersDetail.displayName || this.anomalousUsersDetail.distinctId.substring(0, 17) + `...`;
    }
  }

  get propertyType() {
    if (this.propertySegmentsDetail) {
      return this.propertySegmentsDetail.propertyType;
    }
    return undefined;
  }

  generatePermalink(absolute = false, shared = false) {
    const alertCopy = cloneDeep(this.alert);
    if (this.propertySegmentsDetail) {
      const name = this.property;
      const type = this.propertyType;
      const groupByProperties = [
        {
          property: {
            name,
            type,
            typecast: `STRING`,
            source: `EVENTS`,
          },
        },
      ];
      alertCopy.anomaly.insightsQuery.groupByProperties = groupByProperties;
      return generatePermalink(
        {
          alert: alertCopy,
          projectId: this.projectId,
          selectedExplains: {
            property: this.property,
            propertyValue: this.propertyValue,
          },
          shared,
          workspaceId: this.workspaceId,
        },
        absolute,
      );
    } else {
      const projectOrWorkspace = this.workspaceId ? `workspace/${this.workspaceId}` : `${this.projectId}`;
      return (
        `/report/${projectOrWorkspace}/explore/#user?distinct_id=` +
        encodeURIComponent(this.anomalousUsersDetail.distinctId)
      );
    }
  }
}
